import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Card, Form, Input, InputNumber, Select, Spin, Switch, Table, Tag, Typography, message } from 'antd';
import { useSelector } from 'react-redux';
import axios from 'axios';
import config from '../../config';

const { Title, Text, Paragraph } = Typography;

const WhatsAppSettings = () => {
  const DOMAIN = config.REACT_APP_SERVER_DOMAIN;
  const role = useSelector((state) => state.user?.currentUser?.role);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [configForm] = Form.useForm();
  const [loyaltyForm] = Form.useForm();

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${DOMAIN}/api/whatsapp/settings`);
      setSettings(data);
      configForm.setFieldsValue(data.configuration || {});
      loyaltyForm.setFieldsValue(data.loyaltyRule || {});
    } catch (error) {
      message.error(error?.response?.data?.error || 'Impossible de charger WhatsApp & fidélité.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const templates = useMemo(() => {
    const values = {};
    (settings?.templates || []).forEach((template) => { values[template.event_key] = template; });
    return values;
  }, [settings]);

  const saveConfig = async (values) => {
    setSaving(true);
    try {
      await axios.put(`${DOMAIN}/api/whatsapp/configuration`, values);
      message.success('Configuration WhatsApp enregistrée.');
      await load();
    } catch (error) {
      message.error(error?.response?.data?.error || 'Configuration non enregistrée.');
    } finally {
      setSaving(false);
    }
  };

  const saveLoyalty = async (values) => {
    setSaving(true);
    try {
      await axios.put(`${DOMAIN}/api/whatsapp/loyalty/rule`, values);
      message.success('Règles de fidélité enregistrées.');
      await load();
    } catch (error) {
      message.error(error?.response?.data?.error || 'Règles de fidélité non enregistrées.');
    } finally {
      setSaving(false);
    }
  };

  const updateAutomation = async (row, changes) => {
    try {
      await axios.put(`${DOMAIN}/api/whatsapp/automation/${row.event_key}`, { ...row, ...changes });
      await load();
    } catch (error) {
      message.error(error?.response?.data?.error || 'Service non mis à jour.');
    }
  };

  const saveTemplate = async (eventKey, bodyText, templateName) => {
    if (!bodyText?.trim()) return message.warning('Le texte du message ne peut pas être vide.');
    try {
      await axios.put(`${DOMAIN}/api/whatsapp/template/${eventKey}`, {
        body_text: bodyText.trim(),
        template_name: templateName || null,
      });
      message.success('Modèle de message enregistré.');
      await load();
    } catch (error) {
      message.error(error?.response?.data?.error || 'Modèle non enregistré.');
    }
  };

  if (role !== 'admin') {
    return <div style={{ padding: 24 }}><Alert type="warning" showIcon message="Accès administrateur requis" description="Les réglages WhatsApp et fidélité sont réservés à l’administrateur." /></div>;
  }

  if (loading) return <div style={{ padding: 32, textAlign: 'center' }}><Spin size="large" /></div>;

  const columns = [
    {
      title: 'Service',
      dataIndex: 'label',
      render: (value, row) => <><Text strong>{value}</Text><br /><Text type="secondary">{row.description}</Text></>,
    },
    {
      title: 'Activer',
      width: 110,
      render: (_, row) => <Switch checked={Boolean(row.is_enabled)} onChange={(checked) => updateAutomation(row, { is_enabled: checked })} checkedChildren="Oui" unCheckedChildren="Non" />,
    },
    {
      title: 'Mode',
      width: 190,
      render: (_, row) => <Select value={row.send_mode} style={{ width: '100%' }} onChange={(send_mode) => updateAutomation(row, { send_mode })} options={[{ value: 'automatic', label: 'Automatique' }, { value: 'manual_review', label: 'À valider avant envoi' }]} />,
    },
    {
      title: 'Consentement',
      width: 150,
      render: (_, row) => <Switch checked={Boolean(row.requires_opt_in)} onChange={(checked) => updateAutomation(row, { requires_opt_in: checked })} checkedChildren="Requis" unCheckedChildren="Libre" />,
    },
  ];

  return (
    <div style={{ padding: 24, maxWidth: 1240, margin: '0 auto' }}>
      <Title level={2} style={{ marginBottom: 0 }}>WhatsApp & fidélité</Title>
      <Paragraph type="secondary">Activez ou désactivez chaque service indépendamment. Aucun message ne part si le service global ou le scénario concerné reste désactivé.</Paragraph>

      {!settings?.configuration?.token_configured && (
        <Alert type="warning" showIcon message="Connexion WhatsApp non finalisée" description="Le jeton du fournisseur reste à renseigner uniquement sur le serveur avant tout envoi réel." style={{ marginBottom: 16 }} />
      )}

      <Card title="Connexion WhatsApp" style={{ marginBottom: 16 }}>
        <Form layout="vertical" form={configForm} onFinish={saveConfig}>
          <Form.Item label="Service WhatsApp activé" name="is_enabled" valuePropName="checked"><Switch checkedChildren="Activé" unCheckedChildren="Désactivé" /></Form.Item>
          <Form.Item label="Fournisseur" name="provider"><Select options={[{ value: 'none', label: 'Non configuré' }, { value: 'manual_link', label: 'Lien WhatsApp manuel' }, { value: 'meta_cloud_api', label: 'WhatsApp Business Platform' }]} /></Form.Item>
          <Form.Item label="Nom affiché" name="display_name"><Input placeholder="Ndoé Boutique" /></Form.Item>
          <Form.Item label="Identifiant du numéro WhatsApp" name="phone_number_id"><Input placeholder="À renseigner lors du raccordement" /></Form.Item>
          <Form.Item label="Identifiant du compte Business" name="business_account_id"><Input /></Form.Item>
          <Form.Item label="Indicatif par défaut" name="default_country_code"><Input placeholder="243" /></Form.Item>
          <Button type="primary" htmlType="submit" loading={saving}>Enregistrer la connexion</Button>
        </Form>
      </Card>

      <Card title="Services automatiques" style={{ marginBottom: 16 }}>
        <Table rowKey="event_key" dataSource={settings?.automations || []} columns={columns} pagination={false} scroll={{ x: 820 }} />
      </Card>

      <Card title="Programme fidélité" style={{ marginBottom: 16 }}>
        <Paragraph type="secondary">Les points sont crédités à partir d’une vente confirmée, jamais à partir d’un simple départ en livraison.</Paragraph>
        <Form layout="vertical" form={loyaltyForm} onFinish={saveLoyalty}>
          <Form.Item label="Programme fidélité activé" name="is_enabled" valuePropName="checked"><Switch checkedChildren="Activé" unCheckedChildren="Désactivé" /></Form.Item>
          <Form.Item label="Points fixes par achat" name="points_per_purchase"><InputNumber inputMode="decimal" min={0} style={{ width: '100%' }} /></Form.Item>
          <Form.Item label="Points par unité monétaire" name="points_per_currency_unit"><InputNumber inputMode="decimal" min={0} step={0.0001} style={{ width: '100%' }} /></Form.Item>
          <Form.Item label="Montant minimum pour gagner des points" name="minimum_order_amount"><InputNumber inputMode="decimal" min={0} style={{ width: '100%' }} /></Form.Item>
          <Button type="primary" htmlType="submit" loading={saving}>Enregistrer les règles fidélité</Button>
        </Form>
      </Card>

      <Card title="Modèles de messages">
        <Text type="secondary">Variables disponibles : {'{{client_name}}'}, {'{{order_number}}'}, {'{{order_total}}'}, {'{{delivery_date}}'}, {'{{delivery_note}}'}, {'{{points_earned}}'}, {'{{points_balance}}'}.</Text>
        {(settings?.automations || []).map((automation) => {
          const template = templates[automation.event_key];
          return (
            <Card key={automation.event_key} type="inner" title={automation.label} style={{ marginTop: 12 }}>
              <Input defaultValue={template?.template_name || ''} placeholder="Nom du modèle approuvé côté fournisseur, si requis" style={{ marginBottom: 8 }} id={`template-name-${automation.event_key}`} />
              <Input.TextArea defaultValue={template?.body_text || ''} autoSize={{ minRows: 3, maxRows: 6 }} id={`template-body-${automation.event_key}`} />
              <div style={{ marginTop: 8 }}>
                <Button onClick={() => saveTemplate(
                  automation.event_key,
                  document.getElementById(`template-body-${automation.event_key}`)?.value,
                  document.getElementById(`template-name-${automation.event_key}`)?.value,
                )}>Enregistrer le message</Button>
                <Tag color={automation.is_enabled ? 'green' : 'default'} style={{ marginLeft: 8 }}>{automation.is_enabled ? 'Activé' : 'Désactivé'}</Tag>
                {automation.send_mode === 'manual_review' && <Tag color="gold">Validation avant envoi</Tag>}
              </div>
            </Card>
          );
        })}
      </Card>
    </div>
  );
};

export default WhatsAppSettings;
